import { Controller } from "@hotwired/stimulus"

// One inline-editable board cell (sheet parity: pitched_by and the two
// scheduled times). Click swaps in the server-rendered control; Enter or
// blur saves; Escape cancels. The PATCH goes to the PRT inline door and
// the response is a turbo_stream that re-renders the cell partial, so the
// display can never drift from the server's truth. Failure is VISIBLE
// (committee blocker: a silent rollback on the board that must beat a
// Google Sheet sends everyone back to the sheet).
export default class extends Controller {
  static targets = ["display", "editor", "select", "other", "time"]
  static values = { url: String, field: String, grouped: Boolean }

  open(event) {
    // The row navigates on click; every cell interaction stays in the cell.
    event.stopPropagation()
    if (!this.editorTarget.hidden) return
    this.displayTarget.hidden = true
    this.editorTarget.hidden = false
    if (this.hasTimeTarget) {
      this.timeTarget.focus()
    } else {
      this.selectTarget.focus()
    }
  }

  selectChanged(event) {
    if (this.selectTarget.value === "__other__") {
      this.otherTarget.hidden = false
      this.otherTarget.focus()
    } else {
      this.save(event)
    }
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.save(event)
    } else if (event.key === "Escape") {
      this.cancel()
    }
  }

  cancel() {
    this.saving = false
    this.editorTarget.hidden = true
    this.displayTarget.hidden = false
  }

  save(event) {
    if (event) event.stopPropagation()
    // Escape hides the editor, which blurs the focused input, which lands
    // here: a hidden editor means the user CANCELLED, never save (Seat 2
    // reconvene blocker: Escape must not write).
    if (this.editorTarget.hidden) return
    if (this.saving) return
    this.saving = true

    let value
    if (this.hasTimeTarget) {
      value = this.timeTarget.value
    } else if (!this.otherTarget.hidden) {
      value = this.otherTarget.value.trim()
      if (value === "") return this.cancel()
    } else {
      value = this.selectTarget.value
      if (value === "__other__") { this.saving = false; return }
    }

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html"
      },
      body: JSON.stringify({ field: this.fieldValue, value: value, grouped: this.groupedValue })
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((stream) => window.Turbo.renderStreamMessage(stream))
      .catch(() => {
        this.cancel()
        this.toast("Could not save. Your change was not kept. Try again.")
      })
  }

  toast(message) {
    const el = document.createElement("div")
    el.textContent = message
    el.className = "fixed bottom-6 right-6 z-50 px-4 py-3 bg-[#2D2D2D] text-white text-sm rounded-lg shadow-lg"
    document.body.appendChild(el)
    setTimeout(() => el.remove(), 5000)
  }
}
